// Gantt chart model for the board's Gantt view (TPT591, pages/gantt.js): the day-column time axis,
// one lane per sprint and a bar per task. Pure (no DOM, no api.js) so node:test can import it
// without stubs — same shape as lib/sprint-dates.js and lib/due-tasks.js, whose calendar-day rules
// it shares: `YYYY-MM-DD` strings (sprints.start_date/end_date, tasks.due_date) against the
// viewer's LOCAL calendar day. Day arithmetic is UTC-only, so a DST change never repeats or drops
// a day column.
//
// Day positions are indexes from the axis start (0 = its first Monday); a bar covers the columns
// `from`..`to`, both inclusive.

import { todayIso, dueDayDiff, shortDay, taskDueState } from './due-tasks.js';
import { sprintSchedule } from './sprint-dates.js';
import { groupLaneTitle } from './task-group-label.js';

const DAY_MS = 86400000;
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

// No visible sprint has a date → the axis is today ± this many days.
export const FALLBACK_DAYS = 30;
// Shortest axis before week padding.
export const MIN_DAYS = 14;
// How far task due dates may stretch the axis past the sprint range, on either side.
export const DUE_REACH_DAYS = 90;

// Midnight-UTC timestamp of a real calendar day, else null (2026-02-30 and 0002-… included —
// Date.UTC would roll the first over and map the second to 1902).
function stamp(iso) {
  const m = typeof iso === 'string' ? ISO_DAY.exec(iso) : null;
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const t = Date.UTC(y, mo, d);
  const date = new Date(t);
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo && date.getUTCDate() === d ? t : null;
}

function fromStamp(t) {
  const d = new Date(t);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

// A usable `YYYY-MM-DD` string, or null (unset or malformed).
function validDay(value) {
  return stamp(value) == null ? null : value;
}

// `YYYY-MM-DD` strings sort as dates; null sorts last.
function compareDays(a, b) {
  if (a === b) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a < b ? -1 : 1;
}

const minDay = (a, b) => (a < b ? a : b);
const maxDay = (a, b) => (a > b ? a : b);

// `iso` moved by `n` whole days, or null for a malformed day.
export function addDays(iso, n) {
  const t = stamp(iso);
  return t == null ? null : fromStamp(t + n * DAY_MS);
}

// 0 = Monday … 6 = Sunday, or null for a malformed day.
export function isoWeekday(iso) {
  const t = stamp(iso);
  return t == null ? null : (new Date(t).getUTCDay() + 6) % 7;
}

// The viewer's local calendar day of a task timestamp (`created_at`/`updated_at` arrive as ISO UTC
// strings, e.g. "2026-10-20T08:15:30.000Z"). A bare `YYYY-MM-DD` passes through; anything
// unparseable is null.
export function localDay(value) {
  if (typeof value !== 'string' || !value) return null;
  if (ISO_DAY.test(value)) return validDay(value);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : todayIso(date);
}

// The time axis. `points` are the dated lanes' sprint dates, `dues` every task due date on the
// board; `includeToday` is set while any dated lane's sprint is still open, so an open (or late)
// sprint never looks finished.
//   1. Core range = min..max of `points` (plus today), or today ± FALLBACK_DAYS with no points.
//   2. At least MIN_DAYS long — the end moves forward.
//   3. Due dates stretch it, at most DUE_REACH_DAYS past the core range on either side.
//   4. Padded out to whole Monday–Sunday weeks.
// Week labels read "Oct 5"; the year is added on the first week and wherever it changes, unless
// the whole axis sits in today's year.
export function ganttAxis({ points = [], dues = [], includeToday = false, today = todayIso() } = {}) {
  const core = points.filter(validDay);
  let start;
  let end;
  let fallback = false;
  if (core.length === 0) {
    start = addDays(today, -FALLBACK_DAYS);
    end = addDays(today, FALLBACK_DAYS);
    fallback = true;
  } else {
    if (includeToday) core.push(today);
    start = core.reduce(minDay);
    end = core.reduce(maxDay);
  }
  if (dueDayDiff(end, start) < MIN_DAYS - 1) end = addDays(start, MIN_DAYS - 1);

  const floor = addDays(start, -DUE_REACH_DAYS);
  const ceiling = addDays(end, DUE_REACH_DAYS);
  for (const due of dues) {
    if (!validDay(due)) continue;
    if (due < start) start = maxDay(due, floor);
    if (due > end) end = minDay(due, ceiling);
  }

  start = addDays(start, -isoWeekday(start));
  end = addDays(end, 6 - isoWeekday(end));
  const days = dueDayDiff(end, start) + 1;

  const thisYear = String(today).slice(0, 4);
  const allThisYear = start.slice(0, 4) === thisYear && end.slice(0, 4) === thisYear;
  const weeks = [];
  let previousYear = null;
  for (let index = 0; index < days; index += 7) {
    const monday = addDays(start, index);
    const year = monday.slice(0, 4);
    const label = shortDay(monday) + (!allThisYear && year !== previousYear ? `, ${year}` : '');
    previousYear = year;
    weeks.push({ index, start: monday, label });
  }

  const todayAt = dueDayDiff(today, start);
  const todayIndex = todayAt != null && todayAt >= 0 && todayAt < days ? todayAt : null;
  return { start, end, days, weeks, today, todayIndex, fallback };
}

// The Gantt view's whole model: `{ axis, lanes }`.
//
// `tasks` are board.js's rows (fields=board), `sprints` its sprint-table rows, `closed` the status
// names that mean done (lib/statuses.js closedNames()), `groupLabel` the project's singular noun.
//
// Lanes come from the rows, like the sprint board's: one per sprint number present in `tasks`.
//   - A sprint row with a start or end date → a dated `sprint` lane, sorted by its start (or end),
//     then end, then number. Its rail runs start → end; a missing end runs to the axis end
//     (`openEnd`), a missing start from the axis start (`openStart`).
//   - Everything else — sprints without dates, sprint numbers with no sprint row, and the backlog
//     (priority 0, "Backlog", last) — lands in ONE trailing `unscheduled` lane, grouped by sprint,
//     newest first.
//
// Task rows (`kind`):
//   - 'bar' from the sprint's start_date (else the task's created_at day) to its due_date. With no
//     due date a done task ends at its updated_at day (board rows carry no completed_at) and an
//     open one runs `openEnd` (dashed) to the lane end: the sprint's end_date, or the axis end for
//     a sprint with only a start. A due date before the start is a one-day bar on the due date.
//   - 'point' for an open-ended task that starts after its lane ended.
//   - 'none' for an open task without a due date in the unscheduled lane, which has no end.
// `overdue` = not done and due before today (taskDueState()). Bars outside the axis are clipped
// (`clipStart`/`clipEnd`); one entirely outside gets `offAxis` 'before'/'after'. Rows sort by
// start, then open-ended last, then end, then display_order DESC; 'none' rows last.
export function buildGanttModel({ tasks = [], sprints = [], closed = [], today = todayIso(), groupLabel = 'Sprint' } = {}) {
  const closedSet = closed instanceof Set ? closed : new Set(closed || []);
  const sprintByNumber = new Map((sprints || []).map(s => [s.number, s]));

  const groups = new Map();
  for (const task of tasks || []) {
    if (!task || task.is_reservation) continue;
    const priority = Number(task.priority) || 0;
    if (!groups.has(priority)) groups.set(priority, []);
    groups.get(priority).push(task);
  }

  const dated = [];
  const undated = [];
  for (const [priority, rows] of groups) {
    const sprint = priority > 0 ? sprintByNumber.get(priority) || null : null;
    const group = {
      priority,
      sprint,
      rows,
      title: priority === 0 ? 'Backlog' : groupLaneTitle(priority, sprint, groupLabel),
      closed: !!(sprint && sprint.status === 'closed'),
      total: rows.length,
      done: rows.filter(t => closedSet.has(t.status)).length,
      start: sprint ? validDay(sprint.start_date) : null,
      end: sprint ? validDay(sprint.end_date) : null,
    };
    if (group.start || group.end) dated.push(group);
    else undated.push(group);
  }

  const points = [];
  for (const g of dated) {
    if (g.start) points.push(g.start);
    if (g.end) points.push(g.end);
  }
  const dues = [];
  for (const rows of groups.values()) {
    for (const t of rows) if (validDay(t.due_date)) dues.push(t.due_date);
  }
  const axis = ganttAxis({ points, dues, includeToday: dated.some(g => !g.closed), today });

  const last = axis.days - 1;
  const clamp = (i) => Math.max(0, Math.min(i, last));
  function span(from, to) {
    const a = dueDayDiff(from, axis.start);
    const b = dueDayDiff(to, axis.start);
    return {
      from: clamp(a),
      to: clamp(b),
      clipStart: a < 0,
      clipEnd: b > last,
      offAxis: b < 0 ? 'before' : (a > last ? 'after' : null),
    };
  }

  function taskRow(task, laneStart, laneEnd) {
    const done = closedSet.has(task.status);
    const dueDate = validDay(task.due_date);
    const due = taskDueState(task, closedSet, today);
    const row = { task, done, overdue: !!(due && due.overdue), due, dueDate, kind: 'none', start: null, end: null, openEnd: false };
    let start = laneStart || localDay(task.created_at) || dueDate;
    if (!start) return row;
    if (dueDate) {
      if (dueDate < start) start = dueDate;
      return { ...row, kind: 'bar', start, end: dueDate, ...span(start, dueDate) };
    }
    if (done) {
      const end = maxDay(localDay(task.updated_at) || start, start);
      return { ...row, kind: 'bar', start, end, ...span(start, end) };
    }
    if (!laneEnd) return row;
    if (laneEnd < start) return { ...row, kind: 'point', start, end: start, openEnd: true, ...span(start, start) };
    return { ...row, kind: 'bar', start, end: laneEnd, openEnd: true, ...span(start, laneEnd) };
  }

  function sortRows(rows) {
    return rows.sort((a, b) =>
      (a.kind === 'none') - (b.kind === 'none')
      || compareDays(a.start, b.start)
      || (a.openEnd - b.openEnd)
      || compareDays(a.end, b.end)
      || (Number(b.task.display_order) || 0) - (Number(a.task.display_order) || 0));
  }

  dated.sort((a, b) =>
    compareDays(a.start || a.end, b.start || b.end)
    || compareDays(a.end || a.start, b.end || b.start)
    || a.priority - b.priority);

  const lanes = dated.map((g) => {
    const schedule = sprintSchedule(g.start, g.end, today);
    const laneEnd = g.end || axis.end;
    return {
      kind: 'sprint',
      priority: g.priority,
      title: g.title,
      sprint: g.sprint,
      start: g.start,
      end: g.end,
      closed: g.closed,
      schedule,
      // TPT589's lane-countdown rule: an open sprint past its end with work remaining.
      late: !g.closed && !!schedule && schedule.phase === 'ended' && g.done < g.total,
      done: g.done,
      total: g.total,
      bar: { ...span(g.start || axis.start, laneEnd), openStart: !g.start, openEnd: !g.end },
      rows: sortRows(g.rows.map(t => taskRow(t, g.start, laneEnd))),
    };
  });

  if (undated.length) {
    undated.sort((a, b) => (a.priority === 0) - (b.priority === 0) || b.priority - a.priority);
    lanes.push({
      kind: 'unscheduled',
      title: 'Unscheduled',
      done: undated.reduce((n, g) => n + g.done, 0),
      total: undated.reduce((n, g) => n + g.total, 0),
      groups: undated.map(g => ({
        priority: g.priority,
        title: g.title,
        sprint: g.sprint,
        closed: g.closed,
        done: g.done,
        total: g.total,
        rows: sortRows(g.rows.map(t => taskRow(t, null, null))),
      })),
    });
  }

  return { axis, lanes };
}
